import _ from 'lodash';
import * as platformEvents from 'platformEvents';
import * as stateManagement from '#packages/stateManagement';
import * as coreBi from '#packages/coreBi';
import type { EditorAPI } from '@/editorAPI';
import type { CompRef } from 'types/documentServices';
import { bi } from '#packages/util';

enum NotifyOrigin {
  PASTE = 'paste',
  DUPLICATE = 'duplicate',
}

const sendBiOnDuplicatePage = (
  editorAPI: EditorAPI,
  componentRef: CompRef,
  origin: NotifyOrigin,
) => {
  const data = editorAPI.components.data.get(componentRef);

  if (data?.appDefinitionId && data?.widgetId) {
    editorAPI.bi.event(coreBi.events.addPanel.WIDGET_ADDED_TO_STAGE, {
      origin,
      app_id: data.appDefinitionId,
      widget_id: data.widgetId,
      component_type: editorAPI.components.getType(componentRef),
      editorType: bi.getEditorTypeForBI(),
    });
  }
};

const getApplicationId = (
  editorAPI: EditorAPI,
  controllerRef: CompRef,
  isTPA?: boolean,
) => {
  const appData = editorAPI.components.data.get(controllerRef);
  const appDefinitionId = isTPA
    ? appData?.appDefinitionId
    : appData?.applicationId;
  return editorAPI.platform.getAppDataByAppDefId(appDefinitionId)
    ?.applicationId;
};

const notifyApplicationEventType = (
  editorAPI: EditorAPI,
  componentRefs: CompRef[],
  originalComponentRefs: CompRef[],
  origin: NotifyOrigin,
) => {
  const isDuplicate = origin === NotifyOrigin.DUPLICATE;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/for-each
  _.forEach(componentRefs, (componentRef, index) => {
    const originalComponentRef = originalComponentRefs[index];
    const isTPA = editorAPI.dsRead.tpa.isTpaByCompType(
      editorAPI.components.getType(componentRef),
    );
    const isApplication =
      stateManagement.components.selectors.isAppWidget(
        componentRef,
        editorAPI.dsRead,
      ) || isTPA;
    const isContainer = editorAPI.components.is.container(componentRef);

    if (isContainer && !isApplication) {
      const children =
        editorAPI.components.getChildren_DEPRECATED_BAD_PERFORMANCE(
          componentRef,
        );
      const originalChildren =
        editorAPI.components.getChildren_DEPRECATED_BAD_PERFORMANCE(
          originalComponentRef,
        );
      notifyApplicationEventType(editorAPI, children, originalChildren, origin);
    } else if (isApplication) {
      const applicationId = getApplicationId(editorAPI, componentRef, isTPA);
      sendBiOnDuplicatePage(editorAPI, componentRef, origin);
      editorAPI.platform.notifyApplication(
        applicationId,
        platformEvents.factory[
          isDuplicate ? 'widgetDuplicated' : 'widgetPasted'
        ]({
          componentRef,
          originalComponentRef,
        }),
      );
    } else {
      const connections =
        editorAPI.platform.controllers.connections.get(componentRef);
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line you-dont-need-lodash-underscore/for-each
      _.forEach(connections, (connection) => {
        const applicationId = getApplicationId(
          editorAPI,
          connection.controllerRef as CompRef,
        );
        sendBiOnDuplicatePage(editorAPI, componentRef, origin);
        editorAPI.platform.notifyApplication(
          applicationId,
          platformEvents.factory[
            isDuplicate
              ? 'connectedComponentDuplicated'
              : 'connectedComponentPasted'
          ]({
            componentRef,
            originalComponentRef,
            connection,
          }),
        );
      });
    }
  });
};

const notifyApplicationAfterComponentPasted = (
  editorAPI: EditorAPI,
  componentRefs: CompRef[],
  originalComponentRefs: CompRef[],
) => {
  notifyApplicationEventType(
    editorAPI,
    componentRefs,
    originalComponentRefs,
    NotifyOrigin.PASTE,
  );
};

const notifyApplicationAfterComponentDuplicated = (
  editorAPI: EditorAPI,
  componentRefs: CompRef[],
  originalComponentRefs: CompRef[],
) => {
  notifyApplicationEventType(
    editorAPI,
    componentRefs,
    originalComponentRefs,
    NotifyOrigin.DUPLICATE,
  );
};

export {
  notifyApplicationAfterComponentPasted,
  notifyApplicationAfterComponentDuplicated,
};
